/* Store signals, after sableau.com (Oct 2026).
   1. Order source: UTM, ttclid/fbclid, landing page, first touch, visit count
      and in-app browser go onto the cart as hidden attributes, so every order
      in Admin shows where the buyer came from (TikTok's pixel misses most).
   2. Tab badge: when the tab is hidden and the bag has items, the title reads
      "Don't forget this..." and the favicon shows the real item count.
   3. Clarity events: pdp_view, add_to_cart, begin_checkout, plus the source
      as a Clarity tag, so recordings can be filtered by them. */
(() => {
  if (window.Shopify && window.Shopify.designMode) return;
  const root = document.currentScript;
  const KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ttclid", "fbclid", "gclid"];
  const SRC = "belnay:source";
  const FIRST = "belnay:first";
  const SRC_TTL = 30 * 864e5;

  const read = (k, store = localStorage) => {
    try { return JSON.parse(store.getItem(k) || "null"); } catch (e) { return null; }
  };
  const write = (k, v, store = localStorage) => {
    try { store.setItem(k, JSON.stringify(v)); } catch (e) {}
  };

  /* Last touch: a visit with campaign params or an outside referrer replaces it. */
  const capture = () => {
    const q = new URLSearchParams(location.search);
    const out = {};
    KEYS.forEach((k) => { const v = q.get(k); if (v) out[k] = v.slice(0, 120); });
    if (!Object.keys(out).length && document.referrer && !document.referrer.includes("//" + location.host)) {
      try {
        out.utm_source = new URL(document.referrer).hostname.replace(/^www\./, "");
        out.utm_medium = "referral";
      } catch (e) {}
    }
    if (out.ttclid && !out.utm_source) out.utm_source = "tiktok";
    if (!Object.keys(out).length) {
      const prev = read(SRC);
      return prev && Date.now() - prev.ts < SRC_TTL ? prev : null;
    }
    out.landing = location.pathname.slice(0, 120);
    out.ts = Date.now();
    write(SRC, out);
    return out;
  };

  const browser = () => {
    const ua = navigator.userAgent || "";
    if (/BytedanceWebview|musical_ly|TikTok/i.test(ua)) return "tiktok-inapp";
    if (/Instagram/i.test(ua)) return "instagram-inapp";
    if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook-inapp";
    if (/Snapchat/i.test(ua)) return "snapchat-inapp";
    return "browser";
  };

  const src = capture();
  let first = read(FIRST);
  if (!first) {
    first = { source: (src && src.utm_source) || "direct", landing: location.pathname.slice(0, 120), ts: Date.now(), visits: 0 };
  }
  if (!read("belnay:seen", sessionStorage)) {
    first.visits += 1;
    write("belnay:seen", 1, sessionStorage);
  }
  write(FIRST, first);

  const attrs = { _browser: browser() };
  if (src) Object.keys(src).forEach((k) => { if (k !== "ts") attrs["_" + k] = String(src[k]); });
  attrs._first_source = String(first.source);
  attrs._first_landing = String(first.landing);
  attrs._first_days = String(Math.floor((Date.now() - first.ts) / 864e5));
  attrs._visits = String(first.visits);

  const getCart = () =>
    fetch("/cart.js", { credentials: "same-origin" }).then((r) => (r.ok ? r.json() : null));

  /* Write the attributes once per session, or again when they changed. */
  const sync = (cart) => {
    if (!cart || !cart.item_count) return;
    const have = cart.attributes || {};
    if (Object.keys(attrs).every((k) => have[k] === attrs[k])) return;
    fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: attrs })
    }).catch(() => {});
  };

  /* Clarity loads from its app embed, sometimes after this script. */
  const clarity = (...args) => {
    let tries = 10;
    const go = () => {
      if (typeof window.clarity === "function") { try { window.clarity(...args); } catch (e) {} }
      else if (tries-- > 0) setTimeout(go, 1000);
    };
    go();
  };
  clarity("set", "source", attrs._utm_source || "direct");
  clarity("set", "browser", attrs._browser);
  if (/\/products\//.test(location.pathname)) clarity("event", "pdp_view");

  document.addEventListener("belnay:cart-add", (e) => {
    clarity("event", "add_to_cart");
    sync(e.detail);
  });
  /* Secure checkout answers the first tap and leaves once. Visitors tapped up to
     7 times in 3 s because nothing moved until the checkout loaded (owner, 2 Nov
     2026). Blocked on click, not with `disabled`: a disabled submit button drops
     name="checkout" from the form and the visitor lands on /cart. */
  let tapped = null;
  let tapTimer = 0;
  const releaseTap = () => {
    clearTimeout(tapTimer);
    if (tapped) { tapped.classList.remove("is-loading"); tapped.removeAttribute("aria-busy"); }
    tapped = null;
  };
  document.addEventListener("click", (e) => {
    const t = e.target instanceof Element ? e.target.closest('[name="checkout"], a[href^="/checkout"]') : null;
    if (!t) return;
    if (tapped) { e.preventDefault(); return; }
    tapped = t;
    t.classList.add("is-loading");
    t.setAttribute("aria-busy", "true");
    clarity("event", "begin_checkout");
    tapTimer = setTimeout(releaseTap, 10000); /* a stalled network gets its button back */
  }, true);
  /* Back from the checkout restores this page from memory, button state included. */
  window.addEventListener("pageshow", (e) => { if (e.persisted) releaseTap(); });

  /* cart.js already asked for the cart while the page loaded. */
  window.addEventListener("load", () => {
    ((window.BelnayCart && window.BelnayCart.ready) || getCart()).then(sync).catch(() => {});
  }, { once: true });

  /* Tab badge. The head carries an icon and a shortcut icon link; Chrome
     picks one of them, so every icon link takes the badge, otherwise the
     unchanged one kept the plain B on the tab (owner, 24 Oct 2026). */
  const icons = Array.from(document.querySelectorAll('link[rel~="icon"]'));
  const icon = icons[0];
  if (!icon || !root) return;
  const setIcon = (href) => icons.forEach((l) => { l.href = href; });
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const away = root.dataset.awayTitle || "Don't forget this...";
  const badgeColor = "#e0262b"; /* Sableau's bright red; the low-stock red read too dark at favicon size (owner, 24 Oct 2026) */
  const orig = icon.href;
  let title = document.title;
  let timer = null;
  let badge = null;

  const draw = (img, n) => {
    const c = document.createElement("canvas");
    c.width = c.height = 64;
    const x = c.getContext("2d");
    if (img) x.drawImage(img, 0, 0, 64, 64);
    x.fillStyle = badgeColor;
    x.beginPath();
    x.arc(42, 22, 22, 0, 7);
    x.fill();
    x.fillStyle = "#fff";
    x.font = "bold 30px Arial, sans-serif";
    x.textAlign = "center";
    x.textBaseline = "middle";
    x.fillText(n > 9 ? "9+" : String(n), 42, 24);
    try { return c.toDataURL("image/png"); } catch (e) { return null; } /* tainted canvas: no CORS on the icon */
  };
  const build = (n, cb) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => cb(draw(img, n) || draw(null, n));
    img.onerror = () => cb(draw(null, n));
    img.src = orig;
  };
  const start = () => {
    getCart().then((cart) => {
      if (!document.hidden || !cart || !cart.item_count) return;
      build(cart.item_count, (url) => {
        if (!document.hidden || !url) return;
        badge = url;
        title = document.title;
        document.title = away;
        setIcon(badge);
        if (reduced) return;
        let on = true;
        timer = setInterval(() => { on = !on; setIcon(on ? badge : orig); }, 1000);
      });
    }).catch(() => {});
  };
  const stop = () => {
    if (timer) clearInterval(timer);
    timer = null;
    if (badge) { setIcon(orig); document.title = title; badge = null; }
  };
  document.addEventListener("visibilitychange", () => (document.hidden ? start() : stop()));
})();
